import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import {
  LayoutDashboard,
  LogOut,
  ChevronDown,
  ChevronUp,
  Pin,
  Briefcase,
  Share2,
  UserCheck,
  CalendarCheck,
  BadgeCheck,
  X,
} from "lucide-react";
import {
  LogoutConsultant,
  GetConsultantName,
} from "../../../Auth/Services/authService";
import NormalAlert from "../../../Alerts/NormalAlert";
import ConfirmationAlert from "../../../Alerts/ConfirmationAlert";
import { useNavigate } from "react-router-dom";

const Sidebar = ({
  isPinned,
  setIsPinned,
  onHoverChange,
  isMobileOpen,
  setIsMobileOpen,
}) => {
  const navigate = useNavigate();
  const hoverTimer = useRef(null);
  const location = useLocation();

  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = isMobileOpen || isPinned || isHovered;

  const [userName, setUserName] = useState(Cookies.get("username") || "User");
  const [employeeRole, setEmployeeRole] = useState(null);

  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isJobMenuOpen, setIsJobMenuOpen] = useState(false);
  const [isCandidateMenuOpen, setIsCandidateMenuOpen] = useState(false);
  const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);

  const [confirmAlert, setConfirmAlert] = useState(null);
  const [alert, setAlert] = useState(null);

  /*  USER INFO  */
  useEffect(() => {
    const fetchUserName = async () => {
      const token = Cookies.get("ConsultantToken");
      if (!token) {
        setUserName("User");
        Cookies.remove("username");
        return;
      }

      try {
        const response = await GetConsultantName(token);
        if (response?.data?.status === "Success") {
          const name = response.data.name;

          Cookies.set("username", name, { secure: true, sameSite: "Strict" });
          setUserName(name);
        } else {
          setUserName("User");
          Cookies.remove("username");
        }
      } catch (error) {
        setAlert({
          type: "fail",
          message:
            error?.response?.data?.message ||
            "Something went wrong. Please try again.",
        });
        setUserName("User");
        Cookies.remove("username");
      }
    };

    fetchUserName();
  }, []);

  /*  LOGOUT  */
  const handleLogout = () => {
    setConfirmAlert({
      type: "warning",
      message: "Are you sure you want to logout?",
    });
  };

  const confirmLogout = async () => {
    try {
      await LogoutConsultant(new Date());
    } catch (error) {
      const safeMessage =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      setAlert({
        type: "fail",
        message: safeMessage,
      });
    } finally {
      Cookies.remove("ConsultantToken");
      Cookies.remove("username");
      navigate("/");
    }
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <>
      <aside
        className={`fixed top-0 left-0 h-screen z-50 bg-gradient-to-b from-[#0d47a1] to-[#42a5f5] text-white flex flex-col transition-all duration-300 ease-in-out
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        style={{ width: isExpanded ? "250px" : "70px" }}
        onMouseEnter={() => {
          if (isPinned) return;

          hoverTimer.current = setTimeout(() => {
            setIsHovered(true);
            onHoverChange?.(true);
          }, 120);
        }}
        onMouseLeave={() => {
          if (isPinned) return;

          clearTimeout(hoverTimer.current);
          setIsHovered(false);
          onHoverChange?.(false);
        }}
      >
        {/*  PROFILE  */}
        <div className="h-[72px] flex items-center border-b border-white/20 px-4">
          {/* ICON COLUMN */}
          <div className="w-10 h-10 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full flex items-center justify-center border border-white/70 font-semibold tracking-wide">
              {getInitials(userName)}
            </div>
          </div>
          {/* TEXT */}
          <div
            className={`
      ml-3 transition-all duration-300
      ${
        isExpanded
          ? "opacity-100 translate-x-0"
          : "opacity-0 -translate-x-2 pointer-events-none"
      }
    `}
          >
            <div className="text-sm font-semibold">Welcome</div>
            <div className="text-xs text-white/70">{userName}</div>
          </div>

          {/* PIN */}
          <div className="ml-auto">
            {/* MOBILE CLOSE BUTTON */}
            <button
              onClick={() => setIsMobileOpen(false)}
              className={`${isMobileOpen ? "block" : "hidden"} md:hidden`}
            >
              <X size={18} />
            </button>

            {/* DESKTOP PIN BUTTON*/}
            <button
              className={`hidden md:block transition-all duration-300 ${isExpanded ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              onClick={() => {
                const next = !isPinned;
                setIsPinned(next);
                Cookies.set("sidebarPinned", next ? "true" : "false");
              }}
            >
              <Pin size={18} />
            </button>
          </div>
        </div>

        {/*  MENU  */}
        <nav
          className={`flex-1 py-2 space-y-2 overflow-y-auto sidebar-scroll ${
            isExpanded ? "px-2" : "px-0"
          }`}
        >
          <MenuItem
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            expanded={isExpanded}
            to="/Consultant/Dashboard"
            setIsMobileOpen={setIsMobileOpen}
          />

          <MenuItem
            icon={<Briefcase size={20} />}
            label="All Jobs"
            expanded={isExpanded}
            to="/Consultant/Dashboard/All-Jobs"
            setIsMobileOpen={setIsMobileOpen}
          />

          <MenuItem
            icon={<Share2 size={20} />}
            label="Profile Shared"
            expanded={isExpanded}
            to="/Consultant/Dashboard/Profile-Shared"
            setIsMobileOpen={setIsMobileOpen}
          />

          <MenuItem
            icon={<UserCheck size={20} />}
            label="Shortlist Candidates"
            expanded={isExpanded}
            to="/Consultant/Dashboard/Shortlisted-Candidates"
            setIsMobileOpen={setIsMobileOpen}
          />

          <MenuItem
            icon={<CalendarCheck size={20} />}
            label="Interview Scheduled"
            expanded={isExpanded}
            to="/Consultant/Dashboard/Interview-Scheduled-Candidates"
            setIsMobileOpen={setIsMobileOpen}
          />

          <MenuItem
            icon={<BadgeCheck size={20} />}
            label="Selected Candidates"
            expanded={isExpanded}
            to="/Consultant/Dashboard/Selected-Candidates"
            setIsMobileOpen={setIsMobileOpen}
          />
        </nav>

        {/*  LOGOUT  */}
        <div className="p-3 border-t border-white/20">
          <button
            onClick={handleLogout}
            className="w-full bg-white text-blue-700 rounded-md flex items-center justify-center gap-2 py-2"
          >
            <LogOut size={18} />
            <span
              className={`transition-all duration-200 ${isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"}`}
            >
              Logout
            </span>
          </button>
        </div>
      </aside>

      {confirmAlert && (
        <ConfirmationAlert
          {...confirmAlert}
          onConfirm={confirmLogout}
          onCancel={() => setConfirmAlert(null)}
        />
      )}

      {alert && <NormalAlert {...alert} onClose={() => setAlert(null)} />}
    </>
  );
};

/*COMPONENTS */
const MenuItem = ({ icon, label, expanded, to, setIsMobileOpen }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    setIsMobileOpen?.(false);

    setTimeout(() => {
      navigate(to);
    }, 300);
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`
        flex items-center rounded-md transition-all duration-200
        ${expanded ? "px-4 gap-3 justify-start" : "px-4 justify-center"}
        hover:bg-white hover:text-blue-800
      `}
      style={{ height: "56px" }}
    >
      <div
        className={`w-10 h-10 flex items-center justify-center shrink-0 ${
          expanded ? "" : "mx-auto"
        }`}
      >
        {icon}
      </div>

      <span
        className={`whitespace-nowrap transition-all duration-200 ${
          expanded
            ? "opacity-100 translate-x-0"
            : "opacity-0 -translate-x-2 w-0 overflow-hidden"
        }`}
      >
        {label}
      </span>
    </Link>
  );
};

export default Sidebar;
